import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { kindLabel, movies, searchContent, tvshows } from "@/lib/content";
import type { ContentKind } from "@/types/media";

const PAGE_SIZE = 8;

type FilterKey = "all" | ContentKind;

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tvshow", label: "电视剧" },
  { key: "news", label: "资讯" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";

  const [keyword, setKeyword] = useState(query);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setKeyword(query);
    setPage(1);
  }, [query]);

  useDocumentMeta({
    title: query ? `“${query}” 的搜索结果 - 夜无疆影视大全` : "搜索影视 - 夜无疆影视大全",
    description: query
      ? `夜无疆为你找到与「${query}」相关的电影、电视剧与影视资讯，支持按类别筛选与分页浏览。`
      : "在夜无疆搜索电影、电视剧与影视资讯，输入关键词即可快速找到你想看的内容。",
    keywords: "影视搜索,电影搜索,电视剧搜索,影视资讯搜索,夜无疆搜索",
  });

  const results = useMemo(() => searchContent(query, filter), [query, filter]);
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = results.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const suggestions = useMemo(() => {
    const pool = [
      ...movies.map((item) => ({ id: item.id, title: item.title, kind: "movie" as ContentKind, popularity: item.popularity })),
      ...tvshows.map((item) => ({ id: item.id, title: item.title, kind: "tvshow" as ContentKind, popularity: item.popularity })),
    ];
    return pool.sort((a, b) => b.popularity - a.popularity).slice(0, 6);
  }, []);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    setPage(1);
    setSearchParams(value ? { q: value } : {});
  };

  return (
    <div className="w-full animate-fadeIn">
      <div className="border-b border-background-200/70 bg-background-100/40 px-4 py-8 md:px-6 lg:px-10 lg:py-10">
        <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "搜索" }]} />
        <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          {query ? `“${query}” 的搜索结果` : "搜索影视内容"}
        </h1>

        <form onSubmit={handleSubmit} className="mt-5 flex w-full max-w-2xl items-center gap-3">
          <div className="flex h-12 flex-1 items-center gap-2 rounded-full border border-background-300/70 bg-background-50/70 px-5 focus-within:border-primary-500/60">
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="输入电影、电视剧或资讯关键词"
              aria-label="搜索关键词"
              className="h-full w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
            />
          </div>
          <button
            type="submit"
            className="flex h-12 cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 text-sm font-label text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </form>
      </div>

      <div className="w-full px-4 py-8 md:px-6 lg:px-10 lg:py-10">
        {query && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <span className="text-sm text-foreground-600">
              共找到 <strong className="text-primary-400">{results.length}</strong> 条与「{query}」相关的内容
            </span>
            <div className="flex items-center gap-1 rounded-full border border-background-200/70 bg-background-100/70 p-1">
              {FILTERS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setFilter(item.key);
                    setPage(1);
                  }}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors duration-200 ${
                    filter === item.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {query && paged.length > 0 && (
          <div className="space-y-3">
            {paged.map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-100/60 p-3 transition-colors duration-300 hover:border-primary-500/60 md:p-4"
              >
                <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} - 夜无疆影视大全`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-950">
                      {kindLabel(item.kind)}
                    </span>
                    {item.year && <span className="text-xs text-foreground-500">{item.year}</span>}
                  </div>
                  <h3 className="mt-1.5 line-clamp-1 font-heading text-sm font-semibold text-foreground-950 transition-colors duration-200 group-hover:text-primary-400 md:text-base">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 line-clamp-2 text-xs leading-6 text-foreground-600">{item.summary}</p>
                </div>
                <i className="ri-arrow-right-s-line hidden text-xl text-foreground-400 transition-colors duration-200 group-hover:text-primary-400 sm:block" />
              </Link>
            ))}
          </div>
        )}

        {query && paged.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100/50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-2xl text-foreground-500">
              <i className="ri-search-eye-line" />
            </span>
            <p className="mt-4 text-sm text-foreground-700">
              没有找到与「{query}」相关的内容，换个关键词试试。
            </p>
          </div>
        )}

        {!query && (
          <div className="rounded-lg border border-dashed border-background-300/70 bg-background-100/50 px-6 py-14 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-2xl text-foreground-500">
              <i className="ri-search-line" />
            </span>
            <p className="mt-4 text-sm text-foreground-700">输入关键词，开始搜索电影、电视剧或影视资讯。</p>
          </div>
        )}

        <Pagination current={current} total={totalPages} onChange={setPage} />

        <div className="mt-12 border-t border-background-200/70 pt-8">
          <h2 className="font-heading text-base font-semibold text-foreground-900">热门推荐</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {suggestions.map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="flex items-center gap-2 rounded-full border border-background-300/70 bg-background-100/60 px-4 py-2 text-sm text-foreground-700 transition-colors duration-200 hover:border-primary-500/60 hover:text-primary-400"
              >
                <span className="text-[11px] text-foreground-500">{kindLabel(item.kind)}</span>
                {item.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}